import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, UserPlus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { DEPARTMENTS } from "@/lib/departments";
import { Field, SelectField, Group, Chips, ChipField, TIMINGS } from "@/components/DiscoveryFormParts";

const LEASES = ["Bail 3-6-9", "Bail précaire", "Murs en propriété", "SCI du dirigeant", "Autre"];
const REASONS = ["Retraite", "Nouveau projet", "Santé", "Désaccord associés", "Autre"];
const DEPENDENCY = ["Faible", "Moyenne", "Forte"];
const POTENTIAL = ["1", "2", "3", "4", "5"];

const Area = ({ id, label, value, onChange, placeholder }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-[#475569] mb-1.5">{label}</div>
    <Textarea data-testid={`visit-${id}`} value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder} className="rounded-none border-gray-300" />
  </div>
);

export const VISIT_LABELS = {
  function: "Fonction", siret: "SIRET", company_name: "Raison sociale", legal_form: "Forme juridique", sector: "Secteur", department: "Département",
  city: "Ville", year_founded: "Création", employees: "Effectif", revenue: "CA (€)", ebitda: "EBITDA (€)", net_result: "Résultat net (€)",
  profitability: "Rentabilité (%)", monthly_rent: "Loyer mensuel (€)", lease_type: "Bail", lease_end: "Échéance du bail", debts: "Endettement (€)",
  stock_value: "Stock (€)", seller_valuation: "Prix souhaité par le cédant (€)", proac_estimate: "Estimation PROAC (€)", competitors: "Concurrents",
  main_clients: "Principaux clients", top_client_share: "Poids du 1er client (%)", suppliers: "Fournisseurs clés", strengths: "Forces",
  weaknesses: "Points de vigilance", reason_for_sale: "Motif de cession", timing: "Horizon", support_duration: "Accompagnement proposé",
  dependency: "Dépendance au dirigeant", potential: "Potentiel (1-5)", notes: "Notes du conseiller",
};

const EMPTY = Object.fromEntries(["first_name", "last_name", "email", "phone", ...Object.keys(VISIT_LABELS)].map((k) => [k, ""]));
const splitName = (full = "") => { const p = full.trim().split(/\s+/); return [p[0] || "", p.slice(1).join(" ")]; };

export default function VisitReportForm({ params, onCreated }) {
  const [first, last] = splitName(params.get("name") || "");
  const [f, setF] = useState({ ...EMPTY, first_name: first, last_name: last, email: params.get("email") || "", phone: params.get("phone") || "" });
  const [sectors, setSectors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const [manualProfit, setManualProfit] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const set = (k) => (v) => { if (k === "profitability") setManualProfit(v !== ""); setErrors((er) => ({ ...er, [k]: undefined })); setF((x) => ({ ...x, [k]: v })); };

  const validate = () => {
    const er = {};
    if (!f.first_name.trim()) er.first_name = "Le prénom est obligatoire.";
    if (!f.last_name.trim()) er.last_name = "Le nom est obligatoire.";
    if (!f.email.trim()) er.email = "L'email est obligatoire.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) er.email = "Adresse email invalide.";
    setErrors(er);
    const keys = Object.keys(er);
    if (keys.length) {
      toast.error(`Champs à compléter ou corriger : ${keys.map((k) => ({ first_name: "Prénom", last_name: "Nom", email: "Email" })[k]).join(", ")}`);
      const el = document.getElementById(`rdv-onsite-${keys[0].replace("_", "-")}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" }); setTimeout(() => el?.focus(), 400);
    }
    return keys.length === 0;
  };
  useEffect(() => { api.get("/meta/reference").then((r) => setSectors(r.data.sectors)).catch(() => {}); }, []);
  useEffect(() => {
    if (manualProfit) return;
    const ca = parseFloat(f.revenue), eb = parseFloat(f.ebitda);
    setF((x) => ({ ...x, profitability: ca > 0 && !Number.isNaN(eb) ? ((eb / ca) * 100).toFixed(1) : "" }));
  }, [f.revenue, f.ebitda]); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = async () => {
    setLooking(true);
    try {
      const { data: c } = await api.get("/company-lookup", { params: { siret: f.siret } });
      const dep = DEPARTMENTS.find((d) => d.endsWith(`(${c.department})`)) || "";
      setF((x) => ({ ...x, siret: c.siret || x.siret, company_name: c.name, legal_form: c.legal_form, sector: c.sector || x.sector, department: dep || x.department, city: c.city, year_founded: c.year_founded ?? "", employees: c.employees ?? "", revenue: c.revenue ?? x.revenue, net_result: c.net_result ?? x.net_result }));
      toast.success("Entreprise trouvée, champs pré-remplis.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLooking(false);
  };

  const submit = async (e) => {
    e.preventDefault(); setServerError("");
    if (!validate()) return;
    setBusy(true);
    const { first_name, last_name, email, phone, ...report } = f;
    try {
      const { data } = await api.post("/admin/onsite-sellers", { first_name: first_name.trim(), last_name: last_name.trim(), email: email.trim(), phone, site_url: window.location.origin, discovery_request_id: params.get("rdv") || "", visit_report: report });
      toast.success(`Fiche de visite enregistrée, compte créé pour ${data.name}`); onCreated(data);
    } catch (err) {
      const msg = (err.response ? formatApiError(err.response.data?.detail) : "Connexion impossible au serveur. Vérifiez votre réseau puis réessayez.") || "L'enregistrement a échoué.";
      setServerError(msg); toast.error(msg);
    }
    setBusy(false);
  };

  const num = (k, label) => <Field id={`visit-${k}`} label={label} type="number" inputMode="decimal" value={f[k]} onChange={set(k)} />;
  return (
    <form onSubmit={submit} noValidate className="space-y-10" data-testid="visit-form">
      <p className="text-xs text-[#475569]" data-testid="visit-required-note">Les champs marqués d'un <span className="text-red-500">*</span> sont obligatoires.</p>
      <Group step="1" title="Le dirigeant">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field id="onsite-first-name" label="Prénom *" value={f.first_name} onChange={set("first_name")} error={errors.first_name} />
          <Field id="onsite-last-name" label="Nom *" value={f.last_name} onChange={set("last_name")} error={errors.last_name} />
          <Field id="onsite-email" label="Email *" type="email" value={f.email} onChange={set("email")} error={errors.email} />
          <Field id="onsite-phone" label="Téléphone" type="tel" value={f.phone} onChange={set("phone")} />
        </div>
        <Field id="visit-function" label="Fonction" value={f.function} onChange={set("function")} placeholder="Gérant, président…" />
      </Group>
      <Group step="2" title="L'entreprise">
        <div className="flex gap-2 items-end">
          <div className="flex-1"><Field id="visit-siret" label="SIRET / SIREN" value={f.siret} onChange={set("siret")} inputMode="numeric" /></div>
          <button type="button" data-testid="visit-siret-lookup" onClick={lookup} disabled={looking || f.siret.replace(/\D/g, "").length < 9} className="h-11 px-4 bg-[#1B2A4A] text-white text-sm inline-flex items-center gap-2 disabled:opacity-40">{looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Pré-remplir</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field id="visit-company_name" label="Raison sociale" value={f.company_name} onChange={set("company_name")} />
          <Field id="visit-legal_form" label="Forme juridique" value={f.legal_form} onChange={set("legal_form")} />
          <SelectField id="visit-sector" label="Secteur d'activité" value={f.sector} onChange={set("sector")} options={sectors} placeholder="Choisir…" />
          <SelectField id="visit-department" label="Département" value={f.department} onChange={set("department")} options={DEPARTMENTS} placeholder="Choisir…" />
          <Field id="visit-city" label="Ville" value={f.city} onChange={set("city")} />
          <div className="grid grid-cols-2 gap-4">{num("year_founded", "Création")}{num("employees", "Effectif")}</div>
        </div>
      </Group>
      <Group step="3" title="Indicateurs financiers">
        <div className="grid sm:grid-cols-3 gap-4">
          {num("revenue", "Chiffre d'affaires (€)")}{num("ebitda", "EBITDA (€)")}{num("net_result", "Résultat net (€)")}
          {num("profitability", "Rentabilité EBITDA (%)")}{num("monthly_rent", "Loyer mensuel (€)")}<Field id="visit-lease_end" label="Échéance du bail" type="date" value={f.lease_end} onChange={set("lease_end")} />
          {num("debts", "Endettement (€)")}{num("stock_value", "Valeur du stock (€)")}{num("seller_valuation", "Prix souhaité cédant (€)")}
        </div>
        <ChipField label="Locaux"><Chips name="lease_type" options={LEASES} value={f.lease_type} onChange={set("lease_type")} /></ChipField>
        {num("proac_estimate", "Estimation de valeur PROAC (€)")}
      </Group>
      <Group step="4" title="Marché & concurrence">
        <Area id="competitors" label="Principaux concurrents" value={f.competitors} onChange={set("competitors")} placeholder="Noms, zone, positionnement…" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Area id="main_clients" label="Principaux clients" value={f.main_clients} onChange={set("main_clients")} />
          <Area id="suppliers" label="Fournisseurs clés" value={f.suppliers} onChange={set("suppliers")} />
          <Area id="strengths" label="Forces" value={f.strengths} onChange={set("strengths")} />
          <Area id="weaknesses" label="Points de vigilance" value={f.weaknesses} onChange={set("weaknesses")} />
        </div>
        {num("top_client_share", "Poids du 1er client dans le CA (%)")}
      </Group>
      <Group step="5" title="Projet de cession">
        <ChipField label="Motif"><Chips name="reason_for_sale" options={REASONS} value={f.reason_for_sale} onChange={set("reason_for_sale")} /></ChipField>
        <ChipField label="Horizon"><Chips name="timing" options={TIMINGS} value={f.timing} onChange={set("timing")} /></ChipField>
        <ChipField label="Dépendance au dirigeant"><Chips name="dependency" options={DEPENDENCY} value={f.dependency} onChange={set("dependency")} /></ChipField>
        <ChipField label="Potentiel du dossier"><Chips name="potential" options={POTENTIAL} value={f.potential} onChange={set("potential")} /></ChipField>
        <Field id="visit-support_duration" label="Accompagnement du repreneur" value={f.support_duration} onChange={set("support_duration")} placeholder="Ex. 6 mois" />
        <Area id="notes" label="Notes du conseiller (privées)" value={f.notes} onChange={set("notes")} />
      </Group>
      {serverError && <div data-testid="visit-server-error" role="alert" className="border border-red-200 bg-red-50 text-red-700 text-sm p-4">{serverError}</div>}
      <button data-testid="onsite-create" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-8 py-4 rounded-full disabled:opacity-50">
        <UserPlus className="w-5 h-5" /> {busy ? "Enregistrement…" : "Enregistrer la fiche de visite et créer le compte"}
      </button>
    </form>
  );
}
